import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

import { setDefaultBankAccountId } from 'pages/dashboard/components/Configuration/configurationSlice';
import HideComponent from 'components/HideComponent';
import { CountryISOCodes } from 'common/countries';
import {
  Input,
  Select,
  RadioGroup,
  NumberInput,
  PostalCode,
} from 'components/Inputs';
import {
  getCityLabel,
  getPostalCodeLabel,
  handleAddressOnSubmit,
} from 'utils/address';
import { useAppDispatch, useAppSelector } from 'hooks';
import AddressState from 'components/AddressState';
import sample from 'assets/images/bank-sample.png';
import { getSupportedCountries } from 'pages/dashboard/selectors';

import { getBankAccountInfoSchema, getBankName } from '../utils';
import { setIsBankAccountOpen } from '../bankAccountsSlice';
import SortCodeInput from './SortCodeInput';
import {
  getSelectedIntegrator,
  getBankAccountTypes,
  getBankAccountInfo,
  getBankAccountId,
  getIsCreatedByCurrentPerson,
  getTGPAccountReferenceId,
  getPlatformAccountReferenceId,
  getBankAccounts,
  getIsConfigurationTab,
} from '../selectors';
import {
  createBankAccount,
  updateBankAccount,
  fetchBankAccountData,
} from '../thunks';
import {
  bankAcceptedCountriesList,
  ACCOUNT_TYPE_NAMES,
} from 'entities/bankAccounts';

const BankAccountInfo = () => {
  const { t } = useTranslation('bank');
  const dispatch = useAppDispatch();
  const selectedIntegrator = useAppSelector(getSelectedIntegrator);
  const isConfigurationTab = useAppSelector(getIsConfigurationTab);
  const bankAccountTypes = useAppSelector(getBankAccountTypes);
  const bankAccountInfo: any = useAppSelector(getBankAccountInfo);
  const bankAccountId: any = useAppSelector(getBankAccountId);
  const bankAccounts: any = useAppSelector(getBankAccounts);
  const isCreatedByCurrentPerson: boolean = useAppSelector(
    getIsCreatedByCurrentPerson,
  );
  const supportedCountries = useAppSelector(getSupportedCountries);
  const tgpaccountReferenceId = useAppSelector(getTGPAccountReferenceId);
  const platformAccountReferenceId = useAppSelector(
    getPlatformAccountReferenceId,
  );
  const isDisabled =
    (tgpaccountReferenceId !== null && tgpaccountReferenceId !== '') ||
    (platformAccountReferenceId !== null && platformAccountReferenceId !== '');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    control,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<FieldValues>({
    defaultValues: bankAccountInfo,
    resolver: yupResolver(getBankAccountInfoSchema(t)),
  });

  useEffect(() => {
    dispatch(fetchBankAccountData(selectedIntegrator?.integratorsId));
  }, [dispatch, selectedIntegrator]);

  const validateAccountNumber = (data: any) => {
    const last4 = data.accountNumber.slice(-4);

    const account = bankAccounts.find(
      (bank) =>
        bank.isActive &&
        bank.bankAccountsId !== bankAccountId &&
        bank.accountNumberLast4 === last4 &&
        bank.banksIntegratorsId === selectedIntegrator?.banksIntegratorsId,
    );

    return account ? false : true;
  };

  const onSubmit = async (formData: any) => {
    const data = handleAddressOnSubmit(formData);

    const accountNumberIsValid = validateAccountNumber(data);

    if (accountNumberIsValid) {
      if (data.countryIsocode === CountryISOCodes.UnitedKingdom) {
        data.routingNumber = '';
        data.achCompanyId = '';
      } else {
        data.sortCode = '';
      }

      const result = await dispatch(
        bankAccountId ? updateBankAccount(data) : createBankAccount(data),
      );

      if (result.error && result.payload) {
        setError('accountNumber', {
          type: 'manual',
          message: t('bank:fields.accountNumber.validation.unique'),
        });
        return;
      }

      if (result.payload && isConfigurationTab) {
        dispatch(setIsBankAccountOpen(false));
        dispatch(
          setDefaultBankAccountId(result.payload.bankAccount?.bankAccountsId),
        );
      }
    } else {
      setError('accountNumber', {
        type: 'manual',
        message: t('bank:fields.accountNumber.validation.unique'),
      });
    }
  };

  const prevCountryRef = useRef(bankAccountInfo.countryIsocode);

  const countryISOCode = watch('countryIsocode');

  useEffect(() => {
    if (prevCountryRef.current !== countryISOCode) {
      // Preselect the first account type available for the newly chosen country.
      const allowedTypeNames =
        ACCOUNT_TYPE_NAMES[countryISOCode] || ACCOUNT_TYPE_NAMES.default;
      const firstType = bankAccountTypes.find((type) =>
        allowedTypeNames.includes(type.name),
      );
      setValue('bankAccountTypesId', firstType?.value ?? 0);
    }

    prevCountryRef.current = countryISOCode;

    if (isSubmitted) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryISOCode]);

  function filterCountriesByBank(bankName: string | null | undefined) {
    const bank = bankAcceptedCountriesList.find((b) => b.bankName === bankName);

    if (!bank) {
      return supportedCountries;
    }

    return supportedCountries.filter((country) =>
      bank.countryList.includes(country.iSOCode),
    );
  }

  const filteredCountries = filterCountriesByBank(
    getBankName(selectedIntegrator?.bankName ?? ''),
  );

  function filterSelectedCountry(countryIsocode: CountryISOCodes) {
    return filteredCountries.find((c) => c.iSOCode === countryIsocode);
  }

  const filteredSelectedCountry = filterSelectedCountry(countryISOCode);

  const dropdownDisabled = filteredCountries.length === 1;

  const allowedTypeNames =
    ACCOUNT_TYPE_NAMES[countryISOCode] || ACCOUNT_TYPE_NAMES.default;

  const filteredBankAccountTypes = bankAccountTypes.filter((type) =>
    allowedTypeNames.includes(type.name),
  );

  return (
    <div className="p-6 flex-1 overflow-auto relative">
      {selectedIntegrator && (
        <div className="h-12 flex items-center justify-center mb-2.5">
          <img
            alt={selectedIntegrator.bankName}
            src={`/assets/images/banks/${getBankName(
              selectedIntegrator.bankName,
            )}.png`}
            className="mx-auto max-w-[200px]"
          />
        </div>
      )}

      <p className="heading-2xl text-center mb-8">{t('modalTitle')}</p>

      <form
        id="bankAccountForm"
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-md mx-auto px-1"
      >
        <Input
          name="accountName"
          label={t('bank:fields.bankName.label')}
          className="mb-6 pb-6 border-b"
          register={register}
          error={errors.accountName}
          autoComplete="one-time-code"
        />
        <Select
          name="countryIsocode"
          label={t('common:fields.country.label')}
          className="mb-6"
          options={filteredCountries}
          setValue={setValue}
          value={filteredSelectedCountry}
          error={errors.countryIsocode}
          register={register}
          withIcon
          disabled={dropdownDisabled}
        />
        <Input
          name="addressLine1"
          label={t('common:fields.address.label')}
          className="mb-6"
          register={register}
          error={errors.addressLine1}
        />
        <Input
          name="city"
          label={t(getCityLabel(countryISOCode))}
          className="mb-6"
          register={register}
          error={errors.city}
        />

        <div className="flex gap-4.5 pb-6 border-b">
          <AddressState
            label={t('common:fields.province.label')}
            name="stateOrProvince"
            className="w-full"
            countryISOCode={countryISOCode}
            value={bankAccountInfo.stateOrProvince}
            error={errors?.stateOrProvince}
            register={register}
            setValue={setValue}
          />
          <PostalCode
            name="postalCode"
            label={t(getPostalCodeLabel(countryISOCode))}
            countryIsocode={countryISOCode}
            className="w-full"
            register={register}
            setValue={setValue}
            shouldValidate={isSubmitted}
            error={errors?.postalCode}
          />
        </div>

        {(countryISOCode === CountryISOCodes.USA ||
          countryISOCode === CountryISOCodes.Canada) && (
          <img src={sample} alt="Sample" className="mt-12 mx-auto" />
        )}

        <RadioGroup
          options={filteredBankAccountTypes}
          checked={bankAccountInfo.bankAccountTypesId}
          controlled
          watch={watch}
          label={t('bank:fields.accountType.label')}
          name="bankAccountTypesId"
          className="my-6"
          optionsClassName="space-x-10"
          register={register}
          error={errors.bankAccountTypesId}
          disabled={isDisabled}
          optionClassName="mr-4"
        />
        <HideComponent
          show={
            filteredSelectedCountry?.iSOCode === CountryISOCodes.USA ||
            filteredSelectedCountry?.iSOCode === CountryISOCodes.Canada
          }
        >
          <NumberInput
            name="routingNumber"
            label={t('bank:fields.routingNumber.label')}
            placeholder={t('bank:fields.routingNumber.placeholder')}
            className="mb-6"
            control={control}
            error={errors.routingNumber}
            type="int"
            maxLength={9}
            allowNegative={false}
            hideValue={
              !isCreatedByCurrentPerson && bankAccountInfo.routingNumber
            }
            readOnly={isDisabled}
            password
          />
        </HideComponent>
        <HideComponent
          show={
            filteredSelectedCountry?.iSOCode === CountryISOCodes.UnitedKingdom
          }
        >
          <SortCodeInput control={control} error={errors?.sortCode} />
        </HideComponent>
        <NumberInput
          name="accountNumber"
          label={t('bank:fields.accountNumber.label')}
          placeholder={t('bank:fields.accountNumber.placeholder')}
          className="mb-6"
          control={control}
          error={errors.accountNumber}
          type="int"
          maxLength={
            filteredSelectedCountry?.iSOCode === CountryISOCodes.UnitedKingdom
              ? 8
              : 17
          }
          allowNegative={false}
          hideValue={!isCreatedByCurrentPerson && bankAccountInfo.accountNumber}
          readOnly={isDisabled}
          password
          autoComplete="one-time-code"
        />
        <HideComponent
          show={
            filteredSelectedCountry?.iSOCode === CountryISOCodes.USA ||
            filteredSelectedCountry?.iSOCode === CountryISOCodes.Canada
          }
        >
          <Input
            name="achCompanyId"
            label={t('bank:fields.achCompanyId.label')}
            className="mb-10"
            type="password"
            register={register}
            error={errors.achCompanyId}
            hideValue={
              !isCreatedByCurrentPerson && bankAccountInfo.achCompanyId
            }
            readOnly={isDisabled}
            optional
          />
        </HideComponent>
      </form>
    </div>
  );
};

export default BankAccountInfo;
